import { ArrowSquareOutIcon, CaretRightIcon } from '@phosphor-icons/react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';

const EASE_SMOOTH_OUT = [0.22, 1, 0.36, 1] as const;
const BURST_MARKS = 10;

/**
 * The moment a post is confirmed live, in the editor footer (design sheet
 * "After Post now", 2026-10-21). The editor stays on the post; the line says
 * it went out and links to it; one primary action offers the next post in the
 * same set, or opens the live post when there is none.
 *
 * Motion (transitions.dev tokens): the check draws in over 500ms
 * (--duration-very-slow, --ease-smooth-out); ten success-green marks burst
 * once from it over 700ms, ONLY when `celebrate` is true — set for a publish
 * the provider confirmed in this session, never on a later reopen; the next
 * step reveals over 400ms with an 8px rise (--duration-slow). Reduced motion:
 * no burst and no movement.
 */
export function PublishedMoment({
  platformLabel,
  postedAtLabel,
  liveUrl,
  celebrate,
  next,
  backToChatHref,
}: {
  platformLabel: string;
  postedAtLabel: string | null;
  liveUrl: string;
  celebrate: boolean;
  next?: { label: string; onOpen: () => void } | null;
  backToChatHref?: string | null;
}) {
  const reduceMotion = useReducedMotion();
  const animate = celebrate && !reduceMotion;
  const openLive = () => window.open(liveUrl, '_blank', 'noopener,noreferrer');

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <span className="relative inline-flex items-center gap-1.5 text-detail text-muted-foreground">
          <span className="relative inline-flex size-3.5 items-center justify-center text-success">
            <svg viewBox="0 0 24 24" className="size-3.5" fill="none" aria-hidden="true">
              <motion.path
                d="M5 12.5l4.5 4.5L19 7.5"
                stroke="currentColor"
                strokeWidth={2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={animate ? { pathLength: 0, opacity: 0 } : false}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: EASE_SMOOTH_OUT }}
              />
            </svg>
            {animate
              ? Array.from({ length: BURST_MARKS }).map((_, i) => {
                  const angle = (i / BURST_MARKS) * Math.PI * 2;
                  const distance = 16 + (i % 3) * 6;
                  return (
                    <motion.span
                      key={i}
                      aria-hidden="true"
                      className="pointer-events-none absolute size-1 rounded-[1px] bg-success"
                      initial={{ x: 0, y: 0, opacity: 1, scale: 1, rotate: 0 }}
                      animate={{
                        x: Math.cos(angle) * distance,
                        y: Math.sin(angle) * distance,
                        opacity: 0,
                        scale: 0.4,
                        rotate: i * 53,
                      }}
                      transition={{ duration: 0.7, ease: EASE_SMOOTH_OUT, delay: (i % 3) * 0.02 }}
                    />
                  );
                })
              : null}
          </span>
          {postedAtLabel ? `Posted ${postedAtLabel}` : 'Posted'}
        </span>
        {next ? (
          <button
            type="button"
            onClick={openLive}
            className="inline-flex items-center gap-1 text-detail text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            View on {platformLabel}
            <ArrowSquareOutIcon className="size-3" weight="regular" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <motion.div
        className="flex flex-col gap-1.5"
        initial={animate ? { opacity: 0, y: 8 } : false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE_SMOOTH_OUT, delay: animate ? 0.35 : 0 }}
      >
        {next ? (
          <Button
            variant="neutral"
            size="sm"
            className="w-full justify-between"
            onClick={next.onOpen}
          >
            <span className="truncate">Next post · {next.label}</span>
            <CaretRightIcon className="size-3.5 shrink-0" weight="bold" aria-hidden="true" />
          </Button>
        ) : (
          <Button variant="neutral" size="sm" className="w-full" onClick={openLive}>
            <ArrowSquareOutIcon className="size-3.5" weight="bold" aria-hidden="true" />
            View on {platformLabel}
          </Button>
        )}
        {backToChatHref ? (
          <Link
            to={backToChatHref}
            className="self-start text-detail text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Back to chat
          </Link>
        ) : null}
      </motion.div>
    </div>
  );
}
